import type { SupabaseClient } from '@supabase/supabase-js';
import type { CertificateType, Database } from '@/lib/supabase/types';

/**
 * The narrowest client this helper needs. Both the browser singleton and a
 * fresh createClient() satisfy it, and tests can pass a bare `{ rpc }`.
 */
export type TemplateTypeRpcClient = Pick<SupabaseClient<Database>, 'rpc'>;

/**
 * Resolve certificate template ids to their type (participant / speaker).
 *
 * Never read `certificate_templates` from the browser for this: the table's
 * SELECT policy opens a template only to its owner, admins, and system
 * templates, so for a participant the read is silently empty and every
 * "is a certificate configured?" decision built on it is wrong. The
 * `get_certificate_template_types` RPC is security definer and exposes
 * nothing but the type of templates that are bound to a ticket.
 *
 * Throws on an RPC error so callers can tell "failed" from "no templates".
 */
export async function fetchCertificateTemplateTypes(
  client: TemplateTypeRpcClient,
  templateIds: ReadonlyArray<string | null | undefined>
): Promise<Record<string, CertificateType>> {
  const ids = Array.from(new Set(templateIds.filter((id): id is string => !!id)));
  if (ids.length === 0) return {};

  const { data, error } = await client.rpc('get_certificate_template_types', {
    p_template_ids: ids,
  });
  if (error) {
    throw new Error(error.message || 'get_certificate_template_types failed');
  }

  const map: Record<string, CertificateType> = {};
  for (const row of (data ?? []) as Array<{ id: string; type: string }>) {
    if (row.type === 'participant' || row.type === 'speaker') {
      map[row.id] = row.type;
    }
  }
  return map;
}
